import type { ComponentProps, FC } from 'react';
import React, { useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import type { TPreApprovalOfferStepData } from 'shared/types/TRankingService';

import type { DELIVERY_FORM_FIELDS } from '../../constants';
import { DELIVERY_FORM_FIELDS_LABELS_CONFIG } from '../../constants';
import type { TMeetingFormFields } from '../../types';

import { SlotSelect } from './SlotSelect';

type Props = Pick<ComponentProps<typeof SlotSelect>, 'slots' | 'skeletonSlotsCount' | 'skeletonWidth'> & {
  name: ValuesOf<typeof DELIVERY_FORM_FIELDS>;
  isLoading: boolean;
  offerData: TPreApprovalOfferStepData;
  onChangeEvent?: (
    offer: TPreApprovalOfferStepData,
    formData: TMeetingFormFields,
    fieldName: keyof TMeetingFormFields,
  ) => void;
};

export const ControlledSlotSelect: FC<Props> = (props) => {
  const { name, isLoading, skeletonSlotsCount, slots, offerData, onChangeEvent } = props;
  const { control, trigger, setValue, getValues } = useFormContext<TMeetingFormFields>();

  const handleSelectSlot = useCallback(
    (value?: string) => {
      if (onChangeEvent) {
        const eventValue = name === 'dateSlot' ? value : slots?.find((s) => s.id === value)?.label || value;
        onChangeEvent(offerData, { ...getValues(), [name]: eventValue }, name);
      }

      setValue(name, value, { shouldDirty: true, shouldTouch: true });
      trigger(name);
    },
    [getValues, name, offerData, onChangeEvent, setValue, trigger, slots],
  );

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <SlotSelect
          label={DELIVERY_FORM_FIELDS_LABELS_CONFIG[name]}
          slots={slots}
          skeletonSlotsCount={skeletonSlotsCount}
          skeletonWidth={80}
          isLoading={isLoading}
          onSelect={handleSelectSlot}
          value={field.value}
        />
      )}
    />
  );
};
